import { create } from 'zustand'
import { IDocument, ReviewFeedback } from '../../interfaces/document'
import { DocTemplateData } from '@interfaces/events'
import { RiskResultsFilters } from '@interfaces/ai'
import { Product, ProductMetadata } from '@interfaces/pricing'

type TemporaryStore = {
  product: Product | null
  setProduct: (products: Product | null) => void

  productMetadata: ProductMetadata | null
  setProductMetadata: (metadata: ProductMetadata | null) => void

  showReviewFeedbackDialog: boolean
  setShowReviewFeedbackDialog: (show: boolean) => void

  reviewFeedback: ReviewFeedback
  setReviewFeedback: (feedback: Partial<ReviewFeedback>) => void
  resetReviewFeedback: () => void

  editingStartTime: number | null
  setEditingStartTime: (time: number | null) => void

  docsOpened: Array<string>
  addDocsOpened: (docId: string) => void

  hideAiTooltip: boolean
  setHideAiTooltip: (hide: boolean) => void

  currentDocumentTemplateData: DocTemplateData | null
  setCurrentDocumentTemplateData: (data: DocTemplateData | null) => void

  currentDocumentMetadata: IDocument['metadata'] | null
  setCurrentDocumentMetadata: (metadata: IDocument['metadata'] | null) => void

  riskResultsFilters: RiskResultsFilters
  setRiskResultsFilters: (filters: Partial<RiskResultsFilters>) => void
  resetRiskResultsFilters: () => void

  activeProcessingBlocks: Record<string, string> // blockId -> text
  addActiveProcessingBlock: (blockId?: string | null, text?: string) => void
  removeActiveProcessingBlock: (blockId?: string | null) => void

  reviewMetadata: any | null
  setReviewMetadata: (metadata: any | null) => void
}

export const defaultReviewFeedback: ReviewFeedback = {
  document_type: 'N/A',
  template_name: 'N/A',
  template_id: 'N/A',
  ai_tool_used: 'N/A',
  selected_text: 'N/A',
  ai_output: 'N/A',
  feedback_source: 'manual_feedback_button',
  time_active_in_editor: '0',
  message: 'N/A',
  user_id: 'N/A',
  email: 'N/A',
  timestamp: 'N/A',
}

export const useTemporaryStore = create<TemporaryStore>()(set => ({
  product: null,
  setProduct: (product: Product | null) => set({ product }),

  productMetadata: null,
  setProductMetadata: (metadata: ProductMetadata | null) => set({ productMetadata: metadata }),

  showReviewFeedbackDialog: false,
  setShowReviewFeedbackDialog: (show: boolean) => set({ showReviewFeedbackDialog: show }),

  currentDocumentTemplateData: null,
  setCurrentDocumentTemplateData: (data: DocTemplateData | null) => set({ currentDocumentTemplateData: data }),

  currentDocumentMetadata: null,
  setCurrentDocumentMetadata: (metadata: IDocument['metadata'] | null) => set({ currentDocumentMetadata: metadata }),

  reviewFeedback: defaultReviewFeedback,
  setReviewFeedback: (feedback: Partial<ReviewFeedback>) =>
    set(state => ({
      ...state,
      reviewFeedback: { ...state.reviewFeedback, ...feedback },
    })),
  resetReviewFeedback: () => set(state => ({ ...state, reviewFeedback: defaultReviewFeedback })),

  editingStartTime: null,
  setEditingStartTime: (time: number | null) => set({ editingStartTime: time }),

  hideAiTooltip: false,
  setHideAiTooltip: (hide: boolean) => {
    set({ hideAiTooltip: hide })

    // If setting to true, automatically reset to false after 1 second
    if (hide) {
      setTimeout(() => {
        set({ hideAiTooltip: false })
      }, 1000)
    }
  },

  docsOpened: [],
  addDocsOpened: (docId: string) =>
    set(state => {
      const newDocsOpened = Array.from(new Set([...state.docsOpened, docId]))
      return { docsOpened: newDocsOpened }
    }),

  riskResultsFilters: {
    riskLevel: 'ALL',
    status: 'PENDING',
    targetBlock: null,
  },
  setRiskResultsFilters: (filters: Partial<RiskResultsFilters>) =>
    set(state => ({
      riskResultsFilters: { ...state.riskResultsFilters, ...filters },
    })),
  resetRiskResultsFilters: () => set({ riskResultsFilters: { riskLevel: 'ALL', status: 'ALL', targetBlock: null } }),

  activeProcessingBlocks: {},
  addActiveProcessingBlock: (blockId?: string | null, text: string = 'Processing...') => {
    if (!blockId) return
    set(state => ({
      activeProcessingBlocks: { ...state.activeProcessingBlocks, [blockId]: text },
    }))
  },
  removeActiveProcessingBlock: (blockId?: string | null) => {
    if (!blockId) return
    set(state => {
      const { [blockId]: _, ...rest } = state.activeProcessingBlocks
      return { activeProcessingBlocks: rest }
    })
  },

  reviewMetadata: null,
  setReviewMetadata: (metadata: any | null) => set({ reviewMetadata: metadata }),
}))
